import { jsPDF } from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';
import { EpaSummary, EpaFinding } from '@/types';

applyPlugin(jsPDF);

// ============================================================
// EPA Report PDF Export — Enhanced Structure
// ============================================================

// Colour palette
const COLOR = {
  primary:   [41, 98, 255]   as [number, number, number],
  critical:  [220, 38, 38]   as [number, number, number],
  high:      [239, 68, 68]   as [number, number, number],
  medium:    [245, 158, 11]  as [number, number, number],
  low:       [34, 197, 94]   as [number, number, number],
  pass:      [34, 197, 94]   as [number, number, number],
  fail:      [239, 68, 68]   as [number, number, number],
  partial:   [245, 158, 11]  as [number, number, number],
  dark:      [30, 30, 30]    as [number, number, number],
  grey:      [100, 100, 100] as [number, number, number],
  lightGrey: [220, 220, 220] as [number, number, number],
  bgLight:   [245, 247, 255] as [number, number, number],
  bgCode:    [240, 240, 240] as [number, number, number],
  white:     [255, 255, 255] as [number, number, number],
};

const CATEGORY_ORDER = [
  'Authentication & Authorization',
  'Input Validation',
  'CORS & Security Headers',
  'Data Exposure & Logging',
  'Error Handling',
  'Dependency & Configuration',
  'API Design',
];

const SEVERITY_ORDER: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };

function stripMarkdown(text: string): string {
  return text
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/`(.*?)`/g, '$1')
    .replace(/#{1,6}\s?/g, '')
    .replace(/\[(.*?)\]\(.*?\)/g, '$1')
    .replace(/>\s?/g, '')
    .trim();
}

function severityColor(sev: string): [number, number, number] {
  switch (sev.toLowerCase()) {
    case 'critical': return COLOR.critical;
    case 'high':     return COLOR.high;
    case 'medium':   return COLOR.medium;
    case 'low':      return COLOR.low;
    default:         return COLOR.grey;
  }
}

function statusColor(status: string): [number, number, number] {
  switch (status) {
    case 'PASS':    return COLOR.pass;
    case 'FAIL':    return COLOR.fail;
    case 'PARTIAL': return COLOR.partial;
    default:        return COLOR.grey;
  }
}

export function exportEpaReportPdf(epaSummary: EpaSummary, appName: string) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth  = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin       = 15;
  const contentWidth = pageWidth - margin * 2;
  let y = 20;

  // ── Utility: ensure space on current page ──
  const addPageIfNeeded = (needed: number) => {
    if (y + needed > pageHeight - 22) {
      doc.addPage();
      y = 20;
    }
  };

  // ── Draw a horizontal compliance bar ──
  const drawComplianceBar = (
    xStart: number,
    yPos: number,
    barWidth: number,
    pct: number,           // 0–100
    fillColor: [number, number, number] = COLOR.primary,
  ) => {
    const barH = 4;
    // Background track
    doc.setFillColor(...COLOR.lightGrey);
    doc.roundedRect(xStart, yPos, barWidth, barH, 1, 1, 'F');
    // Filled portion
    const filled = Math.max(1, (pct / 100) * barWidth);
    doc.setFillColor(...fillColor);
    doc.roundedRect(xStart, yPos, filled, barH, 1, 1, 'F');
    // Reset
    doc.setFillColor(0, 0, 0);
  };

  // ── Section header ──
  const addSectionHeader = (text: string) => {
    addPageIfNeeded(18);
    y += 6;
    doc.setFillColor(...COLOR.primary);
    doc.rect(margin, y - 5, contentWidth, 10, 'F');
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLOR.white);
    doc.text(text, margin + 3, y + 1.5);
    doc.setTextColor(...COLOR.dark);
    y += 10;
  };

  // ── Category sub-header with compliance bar ──
  const addCategoryHeader = (text: string, pct: number) => {
    addPageIfNeeded(20);
    y += 4;
    // Left accent bar
    doc.setFillColor(...COLOR.primary);
    doc.rect(margin, y - 4, 3, 10, 'F');
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLOR.dark);
    doc.text(text, margin + 6, y + 2);
    // Compliance % label
    const pctLabel = `${Math.round(pct)}% compliant`;
    const pctColor: [number, number, number] = pct >= 75 ? COLOR.pass : pct >= 40 ? COLOR.medium : COLOR.fail;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...pctColor);
    doc.text(pctLabel, pageWidth - margin - doc.getTextWidth(pctLabel), y + 2);
    doc.setTextColor(...COLOR.dark);
    y += 8;
    // Compliance bar
    drawComplianceBar(margin + 6, y, contentWidth - 6, pct, pctColor);
    y += 8;
  };

  // ── Finding sub-header ──
  const addFindingHeader = (finding: EpaFinding, num: number) => {
    addPageIfNeeded(14);
    const sc = statusColor(finding.status);
    const sev = finding.severity.toUpperCase();
    // Light tinted background
    doc.setFillColor(sc[0], sc[1], sc[2]);
    doc.setGState(new (doc as any).GState({ opacity: 0.08 }));
    doc.rect(margin + 3, y - 3, contentWidth - 3, 9, 'F');
    doc.setGState(new (doc as any).GState({ opacity: 1 }));
    // Number
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLOR.grey);
    doc.text(`#${num}`, margin + 5, y + 3);
    // Finding text
    doc.setFontSize(9.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLOR.dark);
    const maxW = contentWidth - 55;
    const titleLines = doc.splitTextToSize(stripMarkdown(finding.finding), maxW);
    doc.text(titleLines[0], margin + 13, y + 3);
    // Severity chip (right side)
    const sevColor = severityColor(sev);
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...sevColor);
    doc.text(`[${sev}]`, pageWidth - margin - 30, y + 3);
    // Status chip
    doc.setTextColor(...sc);
    doc.text(`[${finding.status}]`, pageWidth - margin - 14, y + 3);
    doc.setTextColor(...COLOR.dark);
    y += 10;
    // Overflow title lines
    if (titleLines.length > 1) {
      doc.setFontSize(9.5);
      doc.setFont('helvetica', 'bold');
      for (let i = 1; i < titleLines.length; i++) {
        addPageIfNeeded(6);
        doc.text(titleLines[i], margin + 13, y);
        y += 5;
      }
    }
  };

  // ── Labelled field ──
  const addField = (label: string, value: string, indent = margin + 6) => {
    addPageIfNeeded(8);
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLOR.grey);
    doc.text(`${label}:`, indent, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLOR.dark);
    const valLines = doc.splitTextToSize(stripMarkdown(value), contentWidth - (indent - margin) - 25);
    const labelW = doc.getTextWidth(`${label}: `);
    doc.text(valLines[0], indent + labelW, y);
    y += 5;
    for (let i = 1; i < valLines.length; i++) {
      addPageIfNeeded(5);
      doc.text(valLines[i], indent + labelW, y);
      y += 5;
    }
  };

  // ── Body text ──
  const addBodyText = (text: string, indent = margin + 6) => {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLOR.dark);
    const lines = doc.splitTextToSize(stripMarkdown(text), contentWidth - (indent - margin));
    for (const line of lines) {
      addPageIfNeeded(6);
      doc.text(line, indent, y);
      y += 5;
    }
    y += 1;
  };

  // ── Code snippet block ──
  const addCodeBlock = (snippet: string, indent = margin + 6) => {
    const lines = snippet.split('\n').slice(0, 12); // cap at 12 lines per snippet
    const lineH = 4.5;
    const blockH = lines.length * lineH + 4;
    addPageIfNeeded(blockH + 4);
    doc.setFillColor(...COLOR.bgCode);
    doc.setDrawColor(...COLOR.lightGrey);
    doc.roundedRect(indent, y, contentWidth - (indent - margin), blockH, 1, 1, 'FD');
    doc.setFontSize(7.5);
    doc.setFont('courier', 'normal');
    doc.setTextColor(40, 40, 80);
    let cy = y + lineH;
    for (const line of lines) {
      const truncated = line.length > 90 ? line.slice(0, 87) + '...' : line;
      doc.text(truncated, indent + 2, cy);
      cy += lineH;
    }
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLOR.dark);
    y += blockH + 3;
  };

  // ── Thin separator ──
  const addSeparator = (indent = margin + 3) => {
    addPageIfNeeded(4);
    doc.setDrawColor(...COLOR.lightGrey);
    doc.setLineWidth(0.3);
    doc.line(indent, y, pageWidth - margin, y);
    doc.setLineWidth(0.2);
    y += 4;
  };

  // ── Numbered list ──
  const addNumberedList = (items: string[], indent = margin + 8) => {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLOR.dark);
    items.forEach((item, i) => {
      const cleaned = stripMarkdown(item);
      const prefix = `${i + 1}. `;
      const lines = doc.splitTextToSize(prefix + cleaned, contentWidth - (indent - margin));
      for (let li = 0; li < lines.length; li++) {
        addPageIfNeeded(5);
        doc.text(lines[li], li === 0 ? indent : indent + 4, y);
        y += 5;
      }
    });
    y += 2;
  };

  // ── Stamp footers on all pages (called at end) ──
  const stampFooters = () => {
    const total = (doc as any).internal.getNumberOfPages();
    for (let p = 2; p <= total; p++) {
      // skip cover page (page 1)
      doc.setPage(p);
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...COLOR.grey);
      doc.setDrawColor(...COLOR.lightGrey);
      doc.setLineWidth(0.3);
      doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);
      doc.text(`EPA Report  —  ${appName}`, margin, pageHeight - 7);
      doc.text(`Page ${p - 1} of ${total - 1}`, pageWidth - margin - doc.getTextWidth(`Page ${p - 1} of ${total - 1}`), pageHeight - 7);
      doc.setTextColor(...COLOR.dark);
    }
  };

  // ============================================================
  // COVER PAGE (page 1)
  // ============================================================
  const totalFindings  = epaSummary.totalFindings ?? 0;
  const passCount      = epaSummary.passCount ?? 0;
  const failCount      = epaSummary.failCount ?? 0;
  const partialCount   = epaSummary.partialCount ?? 0;
  const overallPct     = totalFindings > 0 ? Math.round((passCount / totalFindings) * 100) : 0;
  const overallColor: [number, number, number] = overallPct >= 75 ? COLOR.pass : overallPct >= 40 ? COLOR.medium : COLOR.fail;

  // Dark header band
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, pageWidth, 60, 'F');

  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLOR.white);
  doc.text('Endpoint Protection Assessment', margin, 28);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'normal');
  doc.text('EPA Security Report', margin, 38);

  // Accent line
  doc.setFillColor(...COLOR.primary);
  doc.rect(margin, 44, 40, 2, 'F');

  // App name + date below band
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(15, 23, 42);
  doc.text(appName, margin, 80);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLOR.grey);
  doc.text(`Generated: ${new Date().toLocaleString()}`, margin, 89);
  doc.setTextColor(...COLOR.dark);

  // Overall compliance score pill
  const pillX = margin;
  const pillY = 100;
  doc.setFillColor(...overallColor);
  doc.roundedRect(pillX, pillY, 55, 14, 3, 3, 'F');
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLOR.white);
  doc.text(`${overallPct}% Compliant`, pillX + 4, pillY + 10);
  doc.setTextColor(...COLOR.dark);

  // Score summary boxes
  const boxes = [
    { label: 'Total',   value: totalFindings,  color: COLOR.primary  },
    { label: 'Pass',    value: passCount,       color: COLOR.pass     },
    { label: 'Fail',    value: failCount,       color: COLOR.fail     },
    { label: 'Partial', value: partialCount,    color: COLOR.partial  },
  ];
  const boxW = 38;
  const boxH = 22;
  const boxGap = 4;
  let bx = margin;
  const by = 125;
  for (const box of boxes) {
    doc.setFillColor(245, 247, 255);
    doc.setDrawColor(...box.color);
    doc.setLineWidth(0.5);
    doc.roundedRect(bx, by, boxW, boxH, 2, 2, 'FD');
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...box.color);
    const valStr = box.value.toString();
    doc.text(valStr, bx + boxW / 2 - doc.getTextWidth(valStr) / 2, by + 13);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLOR.grey);
    doc.text(box.label, bx + boxW / 2 - doc.getTextWidth(box.label) / 2, by + 20);
    bx += boxW + boxGap;
  }
  doc.setTextColor(...COLOR.dark);

  // Severity breakdown on cover
  const sevBoxes = [
    { label: 'Critical', value: epaSummary.criticalCount ?? 0, color: COLOR.critical },
    { label: 'High',     value: epaSummary.highCount ?? 0,     color: COLOR.high     },
    { label: 'Medium',   value: epaSummary.mediumCount ?? 0,   color: COLOR.medium   },
    { label: 'Low',      value: epaSummary.lowCount ?? 0,      color: COLOR.low      },
  ];
  let sx = margin;
  const sy = 160;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLOR.grey);
  doc.text('SEVERITY BREAKDOWN', margin, sy - 4);
  for (const sb of sevBoxes) {
    doc.setFillColor(245, 247, 255);
    doc.setDrawColor(...sb.color);
    doc.setLineWidth(0.5);
    doc.roundedRect(sx, sy, boxW, boxH, 2, 2, 'FD');
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...sb.color);
    const vs = sb.value.toString();
    doc.text(vs, sx + boxW / 2 - doc.getTextWidth(vs) / 2, sy + 13);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLOR.grey);
    doc.text(sb.label, sx + boxW / 2 - doc.getTextWidth(sb.label) / 2, sy + 20);
    sx += boxW + boxGap;
  }
  doc.setTextColor(...COLOR.dark);

  // Overall compliance bar on cover
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLOR.grey);
  doc.text('OVERALL COMPLIANCE', margin, 198);
  drawComplianceBar(margin, 200, contentWidth, overallPct, overallColor);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...overallColor);
  doc.text(`${overallPct}%`, margin + contentWidth + 2, 204);
  doc.setTextColor(...COLOR.dark);

  // Table of contents on cover
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLOR.grey);
  doc.text('CONTENTS', margin, 218);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLOR.dark);
  const toc = [
    '1. Executive Summary',
    '2. Category Overview',
    '3. Category Deep-Dive  (all findings with evidence & fix guidance)',
    '4. Prioritised Remediation Plan',
    epaSummary.endpointInventory ? '5. Endpoint Inventory' : '',
  ].filter(Boolean);
  let ty = 224;
  for (const line of toc) {
    doc.text(line, margin + 3, ty);
    ty += 6;
  }

  // ============================================================
  // Start content on page 2
  // ============================================================
  doc.addPage();
  y = 20;

  // ============================================================
  // SECTION 1 — EXECUTIVE SUMMARY
  // ============================================================
  addSectionHeader('1. Executive Summary');

  // Scorecard table
  const summaryData = [
    ['Total Findings', totalFindings.toString()],
    ['Pass',           passCount.toString()],
    ['Fail',           failCount.toString()],
    ['Partial',        partialCount.toString()],
  ];
  (doc as any).autoTable({
    startY: y,
    head: [['Metric', 'Count']],
    body: summaryData,
    margin: { left: margin, right: margin },
    styles: { fontSize: 10, cellPadding: 3.5 },
    headStyles: { fillColor: COLOR.primary, textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 250, 255] },
    tableWidth: 90,
    didParseCell: (data: any) => {
      if (data.section === 'body' && data.column.index === 1) {
        const label = data.row.raw[0] as string;
        if (label === 'Fail')    data.cell.styles.textColor = COLOR.fail;
        if (label === 'Pass')    data.cell.styles.textColor = COLOR.pass;
        if (label === 'Partial') data.cell.styles.textColor = COLOR.partial;
      }
    },
  });
  y = (doc as any).lastAutoTable.finalY + 6;

  // Severity table
  const severityData = [
    ['Critical', (epaSummary.criticalCount ?? 0).toString()],
    ['High',     (epaSummary.highCount ?? 0).toString()],
    ['Medium',   (epaSummary.mediumCount ?? 0).toString()],
    ['Low',      (epaSummary.lowCount ?? 0).toString()],
  ];
  (doc as any).autoTable({
    startY: y,
    head: [['Severity', 'Count']],
    body: severityData,
    margin: { left: margin, right: margin },
    styles: { fontSize: 10, cellPadding: 3.5 },
    headStyles: { fillColor: [50, 50, 70], textColor: 255, fontStyle: 'bold' },
    tableWidth: 90,
    didParseCell: (data: any) => {
      if (data.section === 'body' && data.column.index === 0) {
        const sev = data.cell.raw as string;
        if (sev === 'Critical') data.cell.styles.textColor = COLOR.critical;
        else if (sev === 'High')   data.cell.styles.textColor = COLOR.high;
        else if (sev === 'Medium') data.cell.styles.textColor = COLOR.medium;
        else if (sev === 'Low')    data.cell.styles.textColor = COLOR.low;
      }
    },
  });
  y = (doc as any).lastAutoTable.finalY + 8;

  // Overall compliance bar in section
  addPageIfNeeded(16);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLOR.grey);
  doc.text('Overall Compliance', margin, y);
  y += 4;
  drawComplianceBar(margin, y, contentWidth - 20, overallPct, overallColor);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...overallColor);
  doc.text(`${overallPct}%`, margin + contentWidth - 16, y + 4);
  doc.setTextColor(...COLOR.dark);
  y += 12;

  // ============================================================
  // SECTION 2 — CATEGORY OVERVIEW
  // ============================================================
  addSectionHeader('2. Category Overview');

  // Build category stats
  const findingsByCategory = (epaSummary.findings ?? []).reduce((acc, f) => {
    const cat = f.category || 'Other';
    if (!acc[cat]) acc[cat] = [] as EpaFinding[];
    acc[cat].push(f);
    return acc;
  }, {} as Record<string, EpaFinding[]>);

  const sortedCategories = Object.keys(findingsByCategory).sort((a, b) => {
    const ai = CATEGORY_ORDER.indexOf(a);
    const bi = CATEGORY_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

  const overviewRows = sortedCategories.map((cat) => {
    const fs = findingsByCategory[cat];
    const pc = fs.filter(f => f.status === 'PASS').length;
    const fc = fs.filter(f => f.status === 'FAIL').length;
    const ptc = fs.filter(f => f.status === 'PARTIAL').length;
    const pct = fs.length > 0 ? Math.round((pc / fs.length) * 100) : 0;
    return [cat, fs.length.toString(), pc.toString(), fc.toString(), ptc.toString(), `${pct}%`];
  });

  (doc as any).autoTable({
    startY: y,
    head: [['Category', 'Checks', 'Pass', 'Fail', 'Partial', 'Compliance']],
    body: overviewRows,
    margin: { left: margin, right: margin },
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: COLOR.primary, textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 250, 255] },
    columnStyles: {
      0: { cellWidth: 'auto' },
      1: { cellWidth: 16, halign: 'center' },
      2: { cellWidth: 14, halign: 'center' },
      3: { cellWidth: 14, halign: 'center' },
      4: { cellWidth: 16, halign: 'center' },
      5: { cellWidth: 22, halign: 'center', fontStyle: 'bold' },
    },
    didParseCell: (data: any) => {
      if (data.section === 'body') {
        if (data.column.index === 3) data.cell.styles.textColor = COLOR.fail;
        if (data.column.index === 2) data.cell.styles.textColor = COLOR.pass;
        if (data.column.index === 4) data.cell.styles.textColor = COLOR.partial;
        if (data.column.index === 5) {
          const pctVal = parseInt(data.cell.raw as string, 10);
          data.cell.styles.textColor = pctVal >= 75 ? COLOR.pass : pctVal >= 40 ? COLOR.medium : COLOR.fail;
        }
      }
    },
    didDrawCell: (data: any) => {
      if (data.section === 'body' && data.column.index === 5) {
        const pctVal = parseInt(data.cell.raw as string, 10);
        const barColor: [number, number, number] = pctVal >= 75 ? COLOR.pass : pctVal >= 40 ? COLOR.medium : COLOR.fail;
        const bx = data.cell.x + 2;
        const by = data.cell.y + data.cell.height - 3;
        const bw = data.cell.width - 4;
        doc.setFillColor(...COLOR.lightGrey);
        doc.rect(bx, by, bw, 1.5, 'F');
        doc.setFillColor(...barColor);
        doc.rect(bx, by, Math.max(0.5, (pctVal / 100) * bw), 1.5, 'F');
      }
    },
  });
  y = (doc as any).lastAutoTable.finalY + 10;

  // ============================================================
  // SECTION 3 — CATEGORY DEEP-DIVE (all findings)
  // ============================================================
  addSectionHeader('3. Category Deep-Dive');

  let catIndex = 0;
  for (const category of sortedCategories) {
    catIndex++;
    const findings = findingsByCategory[category];
    const passC = findings.filter(f => f.status === 'PASS').length;
    const pct   = findings.length > 0 ? (passC / findings.length) * 100 : 0;

    addCategoryHeader(`3.${catIndex}  ${category}`, pct);

    // Sort findings: FAIL → PARTIAL → PASS, then by severity
    const sorted = [...findings].sort((a, b) => {
      const statusOrder: Record<string, number> = { FAIL: 0, PARTIAL: 1, PASS: 2 };
      const sd = (statusOrder[a.status] ?? 3) - (statusOrder[b.status] ?? 3);
      if (sd !== 0) return sd;
      return (SEVERITY_ORDER[a.severity] ?? 4) - (SEVERITY_ORDER[b.severity] ?? 4);
    });

    let findingNum = 0;
    for (const finding of sorted) {
      findingNum++;

      addFindingHeader(finding, findingNum);

      // Affected file
      if (finding.file) {
        addField('Affected File', `${finding.file}${finding.lineRange ? `:${finding.lineRange}` : ''}`);
      }

      // Details
      if (finding.details) {
        addPageIfNeeded(8);
        doc.setFontSize(8.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLOR.grey);
        doc.text('Details:', margin + 6, y);
        y += 4;
        addBodyText(finding.details);
      }

      // Evidence
      if (finding.evidence && finding.evidence.length > 0) {
        addPageIfNeeded(10);
        doc.setFontSize(8.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLOR.grey);
        doc.text(`Evidence (${finding.evidence.length}):`, margin + 6, y);
        y += 5;

        for (const ev of finding.evidence) {
          addPageIfNeeded(8);
          doc.setFontSize(8);
          doc.setFont('courier', 'normal');
          doc.setTextColor(...COLOR.primary);
          doc.text(`${ev.file}:${ev.line_range}`, margin + 8, y);
          y += 4;

          if (ev.description) {
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...COLOR.dark);
            const descLines = doc.splitTextToSize(stripMarkdown(ev.description), contentWidth - 16);
            for (const dl of descLines) {
              addPageIfNeeded(5);
              doc.text(dl, margin + 8, y);
              y += 4.5;
            }
          }

          if (ev.code_snippet) {
            addCodeBlock(ev.code_snippet, margin + 8);
          }
          y += 1;
        }
      }

      // Recommendations / Fix Guidance
      if (finding.recommendations && finding.recommendations.length > 0) {
        addPageIfNeeded(10);
        doc.setFontSize(8.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(180, 90, 0);
        doc.text(`Recommended Fix (${finding.recommendations.length}):`, margin + 6, y);
        doc.setTextColor(...COLOR.dark);
        y += 5;
        addNumberedList(finding.recommendations);
      } else if (finding.status === 'PASS') {
        addPageIfNeeded(8);
        doc.setFontSize(8.5);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(...COLOR.pass);
        doc.text('No issues found — check passed.', margin + 8, y);
        doc.setTextColor(...COLOR.dark);
        doc.setFont('helvetica', 'normal');
        y += 6;
      }

      addSeparator(margin + 3);
    }
    y += 4;
  }

  // ============================================================
  // SECTION 4 — PRIORITISED REMEDIATION PLAN
  // ============================================================
  addSectionHeader('4. Prioritised Remediation Plan');

  const remediationFindings = (epaSummary.findings ?? [])
    .filter(f => f.status === 'FAIL' || f.status === 'PARTIAL')
    .sort((a, b) => {
      const sd = (SEVERITY_ORDER[a.severity] ?? 4) - (SEVERITY_ORDER[b.severity] ?? 4);
      if (sd !== 0) return sd;
      const statusOrder: Record<string, number> = { FAIL: 0, PARTIAL: 1 };
      return (statusOrder[a.status] ?? 2) - (statusOrder[b.status] ?? 2);
    });

  if (remediationFindings.length === 0) {
    addPageIfNeeded(12);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...COLOR.pass);
    doc.text('All checks passed — no remediation required.', margin, y);
    doc.setTextColor(...COLOR.dark);
    y += 10;
  } else {
    const remediationRows = remediationFindings.map((f, i) => [
      (i + 1).toString(),
      f.severity.toUpperCase(),
      f.status,
      f.category,
      stripMarkdown(f.finding),
      f.recommendations?.length
        ? stripMarkdown(f.recommendations[0])
        : 'Review and remediate',
    ]);

    (doc as any).autoTable({
      startY: y,
      head: [['#', 'Severity', 'Status', 'Category', 'Issue', 'Recommended Action']],
      body: remediationRows,
      margin: { left: margin, right: margin },
      styles: { fontSize: 8, cellPadding: 2.5, overflow: 'linebreak' },
      headStyles: { fillColor: [50, 50, 70], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 248, 252] },
      columnStyles: {
        0: { cellWidth: 8,  halign: 'center' },
        1: { cellWidth: 18, fontStyle: 'bold' },
        2: { cellWidth: 16, fontStyle: 'bold' },
        3: { cellWidth: 32 },
        4: { cellWidth: 'auto' },
        5: { cellWidth: 45 },
      },
      didParseCell: (data: any) => {
        if (data.section === 'body') {
          if (data.column.index === 1) {
            const sev = (data.cell.raw as string).toLowerCase();
            data.cell.styles.textColor = severityColor(sev);
          }
          if (data.column.index === 2) {
            data.cell.styles.textColor = statusColor(data.cell.raw as string);
          }
        }
      },
    });
    y = (doc as any).lastAutoTable.finalY + 10;
  }

  // ============================================================
  // SECTION 5 — ENDPOINT INVENTORY (optional)
  // ============================================================
  if (epaSummary.endpointInventory) {
    addSectionHeader('5. Endpoint Inventory');

    addPageIfNeeded(14);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLOR.dark);
    doc.text(`Total Controllers: ${epaSummary.endpointInventory.totalControllers}`, margin, y);
    y += 6;
    doc.text(`Total Endpoints: ${epaSummary.endpointInventory.totalEndpoints}`, margin, y);
    y += 8;

    if (epaSummary.endpointInventory.controllers?.length > 0) {
      const controllerData = epaSummary.endpointInventory.controllers.slice(0, 50).map(c => [
        c.name,
        c.basePath,
        c.methods.toString(),
      ]);

      (doc as any).autoTable({
        startY: y,
        head: [['Controller', 'Base Path', 'Methods']],
        body: controllerData,
        margin: { left: margin, right: margin },
        styles: { fontSize: 8.5, cellPadding: 2.5 },
        headStyles: { fillColor: COLOR.primary, textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [248, 250, 255] },
        columnStyles: {
          0: { cellWidth: 'auto' },
          1: { cellWidth: 60 },
          2: { cellWidth: 20, halign: 'center' },
        },
      });
      y = (doc as any).lastAutoTable.finalY + 6;
    }
  }

  // ── Stamp footers on all pages after page 1 ──
  stampFooters();

  // ── Save ──
  const fileName = `EPA_Report_${appName.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
}
